import { useCallback, useState } from 'react'
import { styled, Chip } from '@mui/material'
import GitHubIcon from '@mui/icons-material/GitHub'
import EditIcon from '@mui/icons-material/Edit'
import { FormattedMessage } from 'react-intl'
import { CellPropsBase, CellTitle, CellValueArea, FormCell } from '.'
import { Button } from '../../../../components/buttons'
import GitHubUserNameRegistrationDialog from '../../../../components/dialogs/GitHubUserNameRegistrationDialog'

type GitHubUserNameCellProps = {
  value: string | undefined
  onChange?: (v: string | undefined) => void
} & CellPropsBase

export const GitHubUserNameCell = ({
  value,
  onChange,
  cellWidth,
  label,
  help,
  editable = true,
  required,
  edited,
  validationErrors,
}: GitHubUserNameCellProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleOpenDialog = useCallback(() => setIsDialogOpen(true), [])
  const handleCloseDialog = useCallback(() => setIsDialogOpen(false), [])
  const handleSave = useCallback(
    (userName: string) => {
      // 空文字は「未登録」として扱うため undefined に寄せる。
      onChange && onChange(userName || undefined)
      setIsDialogOpen(false)
    },
    [onChange]
  )

  return (
    <FormCell cellWidth={cellWidth}>
      <CellTitle title={label} required={required} help={help} />
      <CellValueArea edited={edited} validationErros={validationErrors}>
        <GitHubUserNameArea>
          {value ? (
            <>
              {/* GitHubユーザー名は状態(ステータス)ではなく識別子の表示なので、
                  ステータス専用の StatusChip ではなく MUI Chip を直接使う。 */}
              <UsernameChip
                icon={<SmallGitHubIcon />}
                label={value}
                variant="outlined"
                size="small"
              />
              {editable && (
                <Button
                  type="button"
                  variant="outlined"
                  colorPattern="monotone"
                  size="s"
                  onClick={handleOpenDialog}
                >
                  <SmallEditIcon />
                  <FormattedMessage id="edit" />
                </Button>
              )}
            </>
          ) : (
            // 読み取り専用で値が無いときは、TextCellやEmailCellと同じく
            // 枠線だけの空欄にする。
            editable && (
              <Button
                type="button"
                variant="outlined"
                colorPattern="monotone"
                size="s"
                onClick={handleOpenDialog}
              >
                <SmallGitHubIcon />
                <FormattedMessage id="github.register.button" />
              </Button>
            )
          )}
        </GitHubUserNameArea>
      </CellValueArea>
      <GitHubUserNameRegistrationDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
        currentUsername={value || ''}
      />
    </FormCell>
  )
}

const GitHubUserNameArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
})
const UsernameChip = styled(Chip)({
  height: '24px',
  fontSize: '12px',
})
const SmallGitHubIcon = styled(GitHubIcon)({
  fontSize: '16px',
})
const SmallEditIcon = styled(EditIcon)({
  fontSize: '14px',
})
